/** 语音增益（1 为原音量） */
const DEFAULT_GAIN = 2.5;

import { voiceDebug, getAudioSnapshot } from "@/utils/voiceDebug";

const wiredMap = new WeakMap();
let audioUnlocked = false;
let unlockPromise = null;
let voiceContext = null;

/** 极短静音 wav，用于解锁浏览器自动播放限制 */
const SILENT_WAV =
  "data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA";

export function isAudioUnlocked() {
  return audioUnlocked;
}

export function setupLouderPlayback(audioEl, gain = DEFAULT_GAIN) {
  if (!audioEl) return null;

  const existing = wiredMap.get(audioEl);
  if (existing && existing.useWebAudio) {
    if (existing.gainNode) {
      existing.gainNode.gain.value = gain;
    }
    voiceDebug("gain", { action: "reuse", gain });
    return existing;
  }
  if (existing && existing.useWebAudio === false) {
    return existing;
  }

  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) {
      wiredMap.set(audioEl, { useWebAudio: false });
      voiceDebug("gain", "无 AudioContext，使用原生播放");
      return null;
    }

    const audioContext = new AudioContext();
    const source = audioContext.createMediaElementSource(audioEl);
    const gainNode = audioContext.createGain();
    gainNode.gain.value = gain;
    source.connect(gainNode);
    gainNode.connect(audioContext.destination);

    const bundle = {
      audioContext,
      gainNode,
      useWebAudio: true,
    };
    wiredMap.set(audioEl, bundle);
    voiceDebug("gain", {
      action: "init",
      gain,
      contextState: audioContext.state,
    });
    return bundle;
  } catch (e) {
    voiceDebug("gain_fail", String(e));
    wiredMap.set(audioEl, { useWebAudio: false });
    return null;
  }
}

async function resumeAudioContext(bundle) {
  if (!bundle || !bundle.audioContext) return;
  const before = bundle.audioContext.state;
  if (bundle.audioContext.state === "suspended") {
    try {
      await bundle.audioContext.resume();
      voiceDebug("context", {
        action: "resume",
        before,
        after: bundle.audioContext.state,
      });
    } catch (e) {
      voiceDebug("context_fail", String(e));
    }
  }
}

function getVoiceContext() {
  if (!voiceContext) {
    voiceContext = require.context("@/assets/voice", false, /\.mp3$/);
  }
  return voiceContext;
}

/** 根据柜号匹配 mp3（支持 17号 -> 017.mp3） */
export function buildVoiceNameCandidates(name) {
  const raw = String(name || "").trim();
  const candidates = [];
  const push = (v) => {
    if (v && !candidates.includes(v)) candidates.push(v);
  };

  push(raw);
  const digits = raw.match(/\d+/g);
  if (digits && digits.length) {
    const num = digits.join("");
    push(num);
    push(num.padStart(2, "0"));
    push(num.padStart(3, "0"));
  }
  const stripped = raw.replace(/[号柜\s]/g, "");
  if (stripped && stripped !== raw) {
    push(stripped);
    const d = stripped.match(/\d+/);
    if (d) {
      push(d[0]);
      push(d[0].padStart(2, "0"));
      push(d[0].padStart(3, "0"));
    }
  }
  return candidates;
}

export function resolveVoiceMp3(name) {
  const ctx = getVoiceContext();
  const keys = ctx.keys();
  const candidates = buildVoiceNameCandidates(name);

  for (const c of candidates) {
    const key = `./${c}.mp3`;
    if (keys.includes(key)) {
      const src = ctx(key);
      voiceDebug("mp3Path", { name, matched: c, ok: true });
      return src;
    }
  }

  voiceDebug("mp3Path", {
    name,
    candidates,
    ok: false,
    hint: "未找到 mp3，请确认 src/assets/voice 下文件名",
  });
  return null;
}

/** 解锁自动播放（触屏/点击一次后生效） */
export async function unlockAudioPlayback(audioEl) {
  if (!audioEl) {
    voiceDebug("unlock_skip", "无 audio 元素");
    return false;
  }
  if (audioUnlocked) {
    voiceDebug("unlock", "已解锁，跳过");
    return true;
  }
  if (unlockPromise) {
    return unlockPromise;
  }

  unlockPromise = (async () => {
    audioEl.volume = 1;
    audioEl.muted = false;
    voiceDebug("unlock_start", getAudioSnapshot(audioEl));

    try {
      const prevSrc = audioEl.currentSrc || audioEl.src || "";
      audioEl.src = SILENT_WAV;
      try {
        await audioEl.play();
      } catch (e) {
        if (e && e.name !== "AbortError") {
          throw e;
        }
        voiceDebug("unlock", "静音 play AbortError 已忽略");
      }
      audioEl.pause();
      audioEl.currentTime = 0;
      if (prevSrc && prevSrc !== SILENT_WAV) {
        audioEl.src = prevSrc;
      } else {
        audioEl.removeAttribute("src");
      }

      audioUnlocked = true;
      const bundle = setupLouderPlayback(audioEl);
      await resumeAudioContext(bundle);
      voiceDebug("unlock_ok", getAudioSnapshot(audioEl));
      return true;
    } catch (e) {
      voiceDebug("unlock_fail", {
        message: e && e.message ? e.message : String(e),
        name: e && e.name,
        snapshot: getAudioSnapshot(audioEl),
      });
      return false;
    } finally {
      unlockPromise = null;
    }
  })();

  return unlockPromise;
}

/** 监听首次触摸/点击以解锁音频（只绑定一次，避免 touch+click 重复） */
export function bindAudioUnlock(audioEl) {
  if (!audioEl) return;
  voiceDebug("bind_unlock", "已绑定 pointerdown 解锁");

  const onUnlock = () => {
    voiceDebug("bind_unlock", "用户交互触发解锁");
    unlockAudioPlayback(audioEl);
  };

  document.addEventListener("pointerdown", onUnlock, {
    once: true,
    capture: true,
  });
}

function waitForCanPlay(audioEl, timeoutMs = 8000) {
  return new Promise((resolve, reject) => {
    if (audioEl.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      voiceDebug("load", { status: "already_ready", readyState: audioEl.readyState });
      resolve();
      return;
    }

    const timer = setTimeout(() => {
      cleanup();
      reject(new Error(`加载超时 ${timeoutMs}ms`));
    }, timeoutMs);

    const onReady = () => {
      cleanup();
      voiceDebug("load", { status: "canplay", readyState: audioEl.readyState });
      resolve();
    };
    const onError = () => {
      cleanup();
      const snap = getAudioSnapshot(audioEl);
      voiceDebug("load_fail", snap);
      reject(
        new Error(
          snap.error
            ? `语音加载失败 code=${snap.error.code} ${snap.error.message}`
            : "语音资源加载失败"
        )
      );
    };
    const cleanup = () => {
      clearTimeout(timer);
      audioEl.removeEventListener("canplay", onReady);
      audioEl.removeEventListener("error", onError);
    };
    audioEl.addEventListener("canplay", onReady);
    audioEl.addEventListener("error", onError);
    voiceDebug("load", { status: "loading", src: audioEl.src });
    audioEl.load();
  });
}

export async function playVoiceAudio(audioEl, src, gain = DEFAULT_GAIN, label = "") {
  voiceDebug("play_start", {
    label: label || "",
    src: src || "",
    unlocked: audioUnlocked,
    snapshot: getAudioSnapshot(audioEl),
  });

  if (!audioEl) {
    voiceDebug("play_fail", "无 audio 元素");
    return false;
  }
  if (!src) {
    voiceDebug("play_fail", "无 src（可能缺少 mp3 文件）");
    return false;
  }

  audioEl.volume = 1;
  audioEl.muted = false;

  if (audioUnlocked) {
    const bundle = setupLouderPlayback(audioEl, gain);
    await resumeAudioContext(bundle);
  } else {
    voiceDebug("play_warn", "尚未解锁，将尝试直接播放（可能无声）");
  }

  audioEl.pause();
  audioEl.currentTime = 0;
  audioEl.src = src;

  try {
    await waitForCanPlay(audioEl);
    await audioEl.play();
    voiceDebug("play_ok", {
      label: label || "",
      snapshot: getAudioSnapshot(audioEl),
    });
    return true;
  } catch (e) {
    voiceDebug("play_fail", {
      label: label || "",
      message: e && e.message ? e.message : String(e),
      name: e && e.name,
      unlocked: audioUnlocked,
      snapshot: getAudioSnapshot(audioEl),
    });
    return false;
  }
}

/** 从接口 gzMessage 等对象中取出要播报的文字 */
const MESSAGE_TEXT_FIELDS = [
  "speech",
  "message",
  "msg",
  "content",
  "text",
  "playMessage",
  "voice",
  "voice_text",
  "notice",
  "desc",
  "remark",
];

export function extractGzMessageText(msg) {
  if (msg == null) return "";
  if (typeof msg === "string") return msg.trim();

  for (const key of MESSAGE_TEXT_FIELDS) {
    const v = msg[key];
    if (v != null && String(v).trim()) return String(v).trim();
  }

  const box =
    msg.name_box != null && String(msg.name_box).trim()
      ? String(msg.name_box).trim()
      : "";
  if (box) return `已打开柜号：${box}`;
  return "";
}

function buildBaiduTtsUrl(text) {
  return `https://tts.baidu.com/text2audio?lan=zh&ie=UTF-8&spd=5&text=${encodeURIComponent(
    text
  )}`;
}

function playWithSpeechSynthesis(text) {
  return new Promise((resolve) => {
    if (!window.speechSynthesis || !window.SpeechSynthesisUtterance) {
      voiceDebug("tts_skip", "无 speechSynthesis");
      resolve(false);
      return;
    }
    try {
      window.speechSynthesis.cancel();
      const utter = new SpeechSynthesisUtterance(text);
      utter.lang = "zh-CN";
      utter.volume = 1;
      utter.rate = 0.92;
      utter.onend = () => {
        voiceDebug("tts_ok", {
          engine: "speechSynthesis",
          text: text.slice(0, 60),
        });
        resolve(true);
      };
      utter.onerror = (e) => {
        voiceDebug("tts_fail", {
          engine: "speechSynthesis",
          err: e && e.error ? e.error : String(e),
        });
        resolve(false);
      };
      window.speechSynthesis.speak(utter);
    } catch (e) {
      voiceDebug("tts_fail", String(e));
      resolve(false);
    }
  });
}

/** 播报文字（优先 TTS 音频 + 增益，失败则用浏览器语音合成） */
export async function playVoiceText(audioEl, text, label = "") {
  const t = String(text || "").trim();
  if (!t) {
    voiceDebug("play_text_fail", "无播报文字");
    return false;
  }
  voiceDebug("play_text_start", { label: label || "", text: t.slice(0, 80) });

  if (audioEl) {
    const url = buildBaiduTtsUrl(t);
    const ok = await playVoiceAudio(
      audioEl,
      url,
      DEFAULT_GAIN,
      label || `tts:${t.slice(0, 24)}`
    );
    if (ok) return true;
  }

  return playWithSpeechSynthesis(t);
}

/** 列出 webpack 打包进的 voice 目录下 mp3（调试用） */
export function listBundledVoiceFiles() {
  try {
    const ctx = getVoiceContext();
    const files = ctx.keys();
    voiceDebug("voice_files", files.length ? files.join(", ") : "voice 目录无 mp3");
    return files;
  } catch (e) {
    voiceDebug("voice_files", `无法读取 voice 目录: ${e}`);
    return [];
  }
}
